import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import api from '../services/api.js'
import router from '../router/index.js'
import { identify, track, reset as resetAnalytics, preparePaywall, newExposureId } from '../analytics.js'
import { clearAccountCache } from '../services/accountCache.js'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(JSON.parse(localStorage.getItem('zoya-user')) || null)
  const token = ref(localStorage.getItem('zoya-token') || null)
  const loading = ref(false)
  const error = ref(null)
  const isAuthOpen = ref(false)
  watch(() => user.value?.id, (id, previous) => {
    if (id !== previous) void clearAccountCache()
  }, { flush: 'sync' })

  // ── Token budget ──────────────────────────────────────────
  const tokenBudget = ref({ used: 0, limit: 150000, remaining: 150000, pct: 0, is_admin: false })
  const showTokenWarning = ref(false)
  const tokenLimitReached = computed(() => !tokenBudget.value.is_admin && tokenBudget.value.pct >= 100)

  // ── Subscription / Paywall ──────────────────────────────
  const showPaywall = ref(false)
  const showGuestLimit = ref(false)
  const showGuestExpiryWarning = ref(false)
  const showLimitPaywall = ref(false)
  // Почему открыт пейвол «Продолжим?»: 'tokens' — кончился дневной лимит,
  // 'guest_voice' — гость истратил три голосовых добавления (api/ai.js,
  // GUEST_VOICE_LIMIT). Сбрасывается в 'tokens' при закрытии попапа.
  const limitPaywallReason = ref('tokens')
  const showTelegramEmailWarning = ref(false)
  // Первый пейвол (lib/freeLimits.js): { trigger: 'photo' | 'voice' | 'chat' | 'analysis' }
  const firstPaywall = ref(null)
  const freeQuota = computed(() => user.value?.free_quota || null)
  // Исчерпан ли бесплатный счётчик — проверка до запроса, чтобы не делать
  // фото и не ждать отказа сервера (403 free_limit ловит перехватчик api.js)
  function quotaExhausted(kind) {
    const q = freeQuota.value
    if (!q?.applies) return false
    if (kind === 'rec') return q.rec_used >= q.rec_limit
    if (kind === 'msg') return q.msg_used >= q.msg_limit
    return false
  }
  let openingPaywall = false
  async function openFirstPaywall(trigger, quota = null) {
    if (openingPaywall || firstPaywall.value) return
    openingPaywall = true
    const id = user.value?.id
    try {
      const assignment = await preparePaywall()
      if (id === user.value?.id) firstPaywall.value = { trigger, at: Date.now(), assignment, exposureId: newExposureId(), quota }
    } finally { openingPaywall = false }
  }
  // Счётчик вырос на клиенте сразу после успешного распознавания или сообщения
  function bumpQuota(kind) {
    const q = user.value?.free_quota
    if (!q?.applies) return
    const next = { ...q }
    if (kind === 'rec') next.rec_used = (next.rec_used || 0) + 1
    if (kind === 'msg') next.msg_used = (next.msg_used || 0) + 1
    user.value = { ...user.value, free_quota: next }
  }
  // Ключ дня, в который пейвол уже показывали сам собой. В localStorage, а не в
  // ref: PWA перезапускается часто, и обычная переменная жила до перезагрузки —
  // «раз в день» на деле означало «раз за сессию».
  const LIMIT_PAYWALL_KEY = 'zoya_limit_paywall_shown'

  const isGuest = computed(() => !!user.value?.is_guest)

  const hasAiAccess = computed(() => {
    const u = user.value
    if (!u) return false
    if (u.is_admin) return true
    // Гости получают ограниченный доступ (квота на распознавания)
    if (u.is_guest) return true
    if (u.is_premium && u.subscription_end &&
        new Date(u.subscription_end) > new Date()) return true
    // Бесплатный триал: 3 дня с момента регистрации
    if (u.created_at) {
      const trialEnd = new Date(u.created_at)
      trialEnd.setDate(trialEnd.getDate() + 3)
      if (new Date() < trialEnd) return true
    }
    return false
  })

  /** День сброса лимита (5:00 MSK) как строка — ключ для ограничения показов */
  function _limitDayKey() {
    const now = new Date()
    const msk = new Date(now.getTime() + 3 * 3600 * 1000)
    if (msk.getUTCHours() < 5) msk.setUTCDate(msk.getUTCDate() - 1)
    return msk.toISOString().slice(0, 10)
  }

  /**
   * Пейвол исчерпанного лимита. Показывается сам собой не чаще раза в сутки —
   * навязчивость на каждом открытии приложения работает против конверсии.
   *
   * Порог 100, а не 90: на девяноста у человека ещё оставалось на пару действий,
   * и попап читался как помеха. Предупредить на подходе — работа жёлтого
   * баннера, а попап уместен там, где действие уже не выполнить.
   *
   * @param {boolean} force — показать несмотря на дневное ограничение. Так
   *   вызывается из перехватчика 429: человек только что попробовал и получил
   *   отказ, это ответ на его действие, а не самостоятельное напоминание.
   */
  function _maybeShowLimitPaywall(pct, force = false) {
    if (!force && localStorage.getItem(LIMIT_PAYWALL_KEY) === _limitDayKey()) return
    const u = user.value
    if (!u || u.is_admin || u.is_guest) return
    // Только если пользователь на бесплатном триале (не премиум)
    if (u.is_premium && u.subscription_end && new Date(u.subscription_end) > new Date()) return
    // Триал ещё не истёк
    if (!u.created_at) return
    const trialEnd = new Date(u.created_at)
    trialEnd.setDate(trialEnd.getDate() + 3)
    if (new Date() >= trialEnd) return
    if (pct >= 100) {
      showLimitPaywall.value = true
      localStorage.setItem(LIMIT_PAYWALL_KEY, _limitDayKey())
    }
  }

  // Отказ сервера — ответ на действие, поэтому окно открывается при каждой
  // попытке. Дневной бюджет проверяет сервер: кэш может устареть после 5:00,
  // а у подписчика фото/голос вообще не ограничены этим бюджетом.
  function openTokenLimitPaywall() {
    const u = user.value
    if (!u || u.is_admin) return
    if (u.is_guest) { showGuestLimit.value = true; return }
    if (u.is_premium && u.subscription_end && new Date(u.subscription_end) > new Date()) {
      showTokenWarning.value = true
      return
    }
    if (!hasAiAccess.value) { showPaywall.value = true; return }
    limitPaywallReason.value = 'tokens'
    showLimitPaywall.value = true
  }

  async function fetchTokenBudget() {
    try {
      const { data } = await api.get('/ai/token-budget')
      tokenBudget.value = data
      if (!data.is_admin && data.pct >= 90) showTokenWarning.value = true
      _maybeShowLimitPaywall(data.pct)
    } catch { /* ignore */ }
  }

  function updateTokenBudget(budgetData) {
    if (budgetData.is_admin) return
    tokenBudget.value = { ...tokenBudget.value, ...budgetData }
    if (budgetData.pct >= 90 || budgetData.warning) showTokenWarning.value = true
    _maybeShowLimitPaywall(budgetData.pct)
  }

  if (token.value) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
  }

  function openAuthPopup() { isAuthOpen.value = true }
  function closeAuthPopup() { isAuthOpen.value = false; error.value = null }

  // Обновить in-memory состояние из localStorage (после OAuth в другой вкладке)
  function restoreSession() {
    const t = localStorage.getItem('zoya-token')
    const u = JSON.parse(localStorage.getItem('zoya-user') || 'null')
    if (t && u) {
      token.value = t
      user.value = u
      api.defaults.headers.common['Authorization'] = `Bearer ${t}`
      identify(u)
    }
  }

  function _saveSession(tokenStr, userData) {
    token.value = tokenStr
    user.value = userData
    localStorage.setItem('zoya-token', tokenStr)
    localStorage.setItem('zoya-user', JSON.stringify(userData))
    api.defaults.headers.common['Authorization'] = `Bearer ${tokenStr}`
    identify(userData)
    const currentPath = router.currentRoute.value.path
    // Новые пользователи (онбординг не пройден) → на онбординг
    // Но если уже на /onboarding — не перенаправляем (иначе ломает Phase 2 flow)
    if (!userData.onboarding_done && currentPath !== '/onboarding') {
      router.push('/onboarding')
    }
    // Возвращающийся пользователь авторизовался на /onboarding → в дневник
    // Не срабатывает при Phase 2 регистрации (linkGuestAccount на /onboarding?phase=2),
    // где StepPlan сам управляет навигацией после сохранения профиля
    if (userData.onboarding_done && currentPath === '/onboarding' && router.currentRoute.value.query.phase !== '2') {
      router.replace('/diary')
    }
  }

  // --- EMAIL + ПАРОЛЬ ---
  async function login(email, password) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/login', { email, password })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'email' })
      closeAuthPopup()
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка входа'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function register(name, email, password) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/register', { name, email, password })
      _saveSession(data.token, data.user)
      track('user_registered', { auth_method: 'email' })
      closeAuthPopup()
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка регистрации'
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- EMAIL (staging dev-login) ---
  async function loginWithEmail(email, password) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/login', { email, password })
      _saveSession(data.token, data.user)
      closeAuthPopup()
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка входа'
    } finally {
      loading.value = false
    }
  }

  async function registerWithEmail(name, email, password) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/register', { name, email, password })
      _saveSession(data.token, data.user)
      closeAuthPopup()
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка регистрации'
    } finally {
      loading.value = false
    }
  }

  // --- OTP (passwordless email) ---
  const otpEmail = ref(null)

  async function sendOtp(email) {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/otp/send', { email })
      otpEmail.value = email
      track('otp_code_requested')
    } catch (e) {
      if (!e.response) {
        error.value = 'Нет соединения с сервером. Проверьте интернет'
      } else {
        error.value = e.response?.data?.error || 'Ошибка отправки кода'
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  async function verifyOtp(email, code) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/otp/verify', { email, code })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'otp' })
      otpEmail.value = null
      closeAuthPopup()
    } catch (e) {
      if (!e.response) {
        error.value = 'Нет соединения с сервером. Проверьте интернет'
      } else {
        error.value = e.response?.data?.error || 'Неверный код'
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- YANDEX ---
  async function loginWithYandex(code, redirect_uri) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/yandex', { code, redirect_uri })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'yandex' })
      closeAuthPopup()
    } catch (e) {
      if (!e.response) {
        error.value = 'Нет соединения с сервером. Проверьте интернет'
      } else {
        error.value = e.response?.data?.detail || 'Ошибка входа через Яндекс'
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- VK ID ---
  // credentials = { code, code_verifier, device_id, redirect_uri, state }
  // Обмен кода делает бэкенд: там лежит защищённый ключ приложения.
  async function loginWithVk(credentials) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/vk', credentials)
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'vk' })
      closeAuthPopup()
    } catch (e) {
      if (!e.response) {
        error.value = 'Нет соединения с сервером. Проверьте интернет'
      } else {
        error.value = e.response?.data?.detail || 'Ошибка входа через VK'
      }
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- GOOGLE ---
  async function loginWithGoogle(code) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/google', { code })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'google' })
      closeAuthPopup()
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка входа через Google'
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- APPLE ---
  async function loginWithApple(appleResponse) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/apple', {
        identityToken: appleResponse.identityToken,
        authorizationCode: appleResponse.authorizationCode,
        user: { email: appleResponse.email, givenName: appleResponse.givenName, familyName: appleResponse.familyName },
      })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'apple' })
      closeAuthPopup()
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка входа через Apple'
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- TELEGRAM ---
  const isTelegramApp = !!window.Telegram?.WebApp?.initData

  // Авто-логин внутри Telegram Mini App
  async function loginWithTelegramInitData() {
    const tg = window.Telegram?.WebApp
    if (!tg?.initData) return false
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/telegram', { initData: tg.initData })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'telegram_app' })
      tg.ready()
      tg.expand()
      closeAuthPopup()
      return true
    } catch (e) {
      error.value = 'Ошибка входа через Telegram'
      return false
    } finally {
      loading.value = false
    }
  }

  // Login Widget на сайте
  async function loginWithTelegram(widgetData) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/telegram', { loginWidget: widgetData })
      _saveSession(data.token, data.user)
      track('user_logged_in', { auth_method: 'telegram' })
      closeAuthPopup()
    } catch (e) {
      error.value = 'Ошибка входа через Telegram'
      throw e
    } finally {
      loading.value = false
    }
  }

  // Привязка Telegram к существующему аккаунту
  async function linkTelegram(widgetData) {
    const { data } = await api.post('/auth/telegram/link', { loginWidget: widgetData })
    user.value = data.user
    localStorage.setItem('zoya-user', JSON.stringify(data.user))
    return data.user
  }

  // Отвязка Telegram
  async function unlinkTelegram() {
    const { data } = await api.delete('/auth/telegram/link')
    user.value = data.user
    localStorage.setItem('zoya-user', JSON.stringify(data.user))
    return data.user
  }

  // --- Привязка email к существующему аккаунту ---
  const linkEmailAddress = ref(null)

  // claim — почта уже занята другим аккаунтом, и человек подтвердил, что это
  // его аккаунт: код уходит туда же, а verify объединяет аккаунты
  async function sendLinkEmail(email, claim = false) {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/email/link', { email, claim })
      linkEmailAddress.value = email
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка отправки кода'
      throw e
    } finally {
      loading.value = false
    }
  }

  async function verifyLinkEmail(email, code, claim = false) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/email/link/verify', { email, code, claim })
      linkEmailAddress.value = null
      if (data.token) {
        // Аккаунты объединены: дальше работаем от аккаунта с почтой. Сессию
        // пересохраняем и перезагружаем страницу — кэши дневника и чата
        // относятся к прежнему аккаунту.
        _saveSession(data.token, data.user)
        window.location.replace('/diary')
        return
      }
      user.value = data.user
      localStorage.setItem('zoya-user', JSON.stringify(data.user))
    } catch (e) {
      error.value = e.response?.data?.error || 'Неверный код'
      throw e
    } finally {
      loading.value = false
    }
  }

  // --- Avatar upload ---
  const avatarThumb = ref(localStorage.getItem('zoya-avatar-thumb') || null)

  async function uploadAvatar(base64, mimeType) {
    const { data } = await api.post('/auth/upload-avatar', { base64, mimeType })
    user.value = data.user
    localStorage.setItem('zoya-user', JSON.stringify(data.user))
    if (base64) {
      const thumb = `data:${mimeType || 'image/jpeg'};base64,${base64}`
      localStorage.setItem('zoya-avatar-thumb', thumb)
      avatarThumb.value = thumb
    }
    return data.user
  }

  // --- Mobile deep link (Capacitor native OAuth callback) ---
  async function loginWithToken(tokenStr) {
    loading.value = true
    error.value = null
    try {
      api.defaults.headers.common['Authorization'] = `Bearer ${tokenStr}`
      const { data } = await api.get('/auth/me')
      _saveSession(tokenStr, data.user)
      track('user_logged_in', { auth_method: 'oauth_mobile' })
      closeAuthPopup()
    } catch (e) {
      delete api.defaults.headers.common['Authorization']
      const detail = e.response?.data?.error || e.message || 'неизвестная ошибка'
      error.value = `Ошибка авторизации: ${detail} (${e.response?.status || 'network'})`
    } finally {
      loading.value = false
    }
  }

  async function checkAuthStatus() {
    // Внутри Telegram Mini App — всегда логин через initData (свежий токен)
    if (isTelegramApp) {
      await loginWithTelegramInitData()
      return
    }
    if (!token.value) return
    try {
      const { data } = await api.get('/auth/me')
      user.value = data.user
      localStorage.setItem('zoya-user', JSON.stringify(user.value))
      identify(data.user)
      // Проверяем истечение гостевого периода (предупреждение за 24ч)
      if (data.user.is_guest && data.user.guest_expires_at) {
        const hoursLeft = (new Date(data.user.guest_expires_at) - Date.now()) / 3600000
        if (hoursLeft > 0 && hoursLeft <= 24 && !sessionStorage.getItem('guest-expiry-dismissed')) {
          showGuestExpiryWarning.value = true
        }
      }
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) {
        logout()
      }
    }
  }

  function applyCommunication(data, owner) {
    if (!user.value || user.value.id !== owner || (user.value.communication_version || 0) > data.version) return
    user.value = { ...user.value, address_form: data.settings.address_form, communication: data.settings, communication_version: data.version }
    localStorage.setItem('zoya-user', JSON.stringify(user.value))
  }

  async function updateProfile(fields) {
    const owner = user.value?.id
    const { data } = await api.patch('/auth/profile', fields)
    if (owner !== user.value?.id || data.user.id !== owner) return data.user
    const current = user.value
    if ((current.communication_version || 0) > (data.user.communication_version || 0)) {
      Object.assign(data.user, { address_form: current.address_form, communication: current.communication, communication_version: current.communication_version })
    }
    user.value = data.user
    localStorage.setItem('zoya-user', JSON.stringify(data.user))
    return data.user
  }

  // ── AI PAL recalculation ──────────────────────────────
  const palLoading = ref(false)

  async function recalcPal(activityData) {
    // Доступ к ИИ здесь больше не нужен: коэффициент считается детерминированно
    // по MET, а модель отвечает только за интенсивность свободной активности.
    // Пока стояла проверка подписки, бесплатный пользователь после правки
    // активности получал не то же число, что при регистрации.
    palLoading.value = true
    try {
      const { data } = await api.post('/ai/activity-pal', { activity_data: activityData })
      if (data.user) {
        user.value = data.user
        localStorage.setItem('zoya-user', JSON.stringify(data.user))
      }
    } catch { /* silent — PAL is best-effort */ }
    finally { palLoading.value = false }
  }

  // Чат открыт: сервер уже снял отметку в /chat/init, значок на иконке Зои
  // (NavBar.vue) гасим сразу, не дожидаясь следующего /auth/me
  function markZoyaSeen() {
    if (!user.value?.unread_zoya) return
    user.value = { ...user.value, unread_zoya: 0 }
    localStorage.setItem('zoya-user', JSON.stringify(user.value))
  }

  // --- ГОСТЕВОЙ АККАУНТ (анонимный онбординг) ---
  async function createGuestSession() {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/guest')
      _saveSession(data.token, data.user)
      track('guest_session_created')
      closeAuthPopup()
      return data.user
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка создания гостевого аккаунта'
      throw e
    } finally {
      loading.value = false
    }
  }

  // Привязка реальных credentials к гостевому аккаунту
  async function linkGuestAccount(method, credentials) {
    loading.value = true
    error.value = null
    try {
      const { data } = await api.post('/auth/link-guest', { method, credentials })
      _saveSession(data.token, data.user)
      track('guest_account_linked', { method, merged: !!data.merged })
      return { user: data.user, merged: !!data.merged }
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка привязки аккаунта'
      throw e
    } finally {
      loading.value = false
    }
  }

  function logout() {
    const reminderOwner = user.value?.id
    if (reminderOwner) void import('../services/reminders.js').then(m => m.detach(reminderOwner))
    track('user_logged_out')
    resetAnalytics()
    token.value = null
    user.value = null
    localStorage.removeItem('zoya-token')
    localStorage.removeItem('zoya-user')
    localStorage.removeItem('zoya-avatar-thumb')
    avatarThumb.value = null
    delete api.defaults.headers.common['Authorization']
    router.push('/diary')
    openAuthPopup()
  }

  async function deleteAccount(appleAuthorizationCode) {
    try {
      await api.delete('/auth/account', { data: { appleAuthorizationCode } })
      track('account_deleted')
      resetAnalytics()
      token.value = null
      user.value = null
      localStorage.removeItem('zoya-token')
      localStorage.removeItem('zoya-user')
      localStorage.removeItem('zoya-avatar-thumb')
      avatarThumb.value = null
      delete api.defaults.headers.common['Authorization']
      router.push('/onboarding')
    } catch (e) {
      error.value = e.response?.data?.error || 'Ошибка удаления аккаунта'
      throw e
    }
  }

  return {
    user, token, loading, error, isAuthOpen, isTelegramApp, isGuest,
    tokenBudget, showTokenWarning, tokenLimitReached,
    showPaywall, showGuestLimit, showGuestExpiryWarning, showLimitPaywall, limitPaywallReason, showTelegramEmailWarning, hasAiAccess,
    firstPaywall, freeQuota, quotaExhausted, openFirstPaywall, bumpQuota,
    _maybeShowLimitPaywall, openTokenLimitPaywall,
    openAuthPopup, closeAuthPopup,
    login, register, otpEmail, sendOtp, verifyOtp,
    loginWithYandex, loginWithVk, loginWithGoogle, loginWithApple, loginWithTelegram, loginWithEmail, registerWithEmail, loginWithToken,
    loginWithTelegramInitData, linkTelegram, unlinkTelegram,
    linkEmailAddress, sendLinkEmail, verifyLinkEmail,
    createGuestSession, linkGuestAccount,
    restoreSession,
    checkAuthStatus, updateProfile, applyCommunication, recalcPal, palLoading, markZoyaSeen, logout, deleteAccount,
    fetchTokenBudget, updateTokenBudget,
    uploadAvatar, avatarThumb,
  }
})
